import { useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';
import {
    ADDRESS_OPTIONS, EMOJI_OPTIONS, ESCALATION_LABELS, LENGTH_OPTIONS, TONE_OPTIONS,
    collectFeedbackHistory, collectFeedbackNow, loadFeedbackSettings, loadFeedbackUsage, previewFeedbackStyle, saveFeedbackSettings,
} from '../../../../services/feedback';
import FeedbackReadiness from './FeedbackReadiness';
import { errorText, money } from './feedbackUi';

// Четыре раздела работы: каждый включается и настраивается отдельно, в каждом доступна полная автоматизация.
const SECTION_CARDS = [
    { id: 'questions', title: 'Вопросы', hint: 'Вопросы покупателей о товарах.', levels: ['question'] },
    { id: 'reviews', title: 'Отзывы', hint: 'Отзывы с текстом. Отзывы без текста (одна оценка) закрываются сами.', levels: ['high', 'mid', 'low'] },
    { id: 'chat', title: 'Чаты', hint: 'Чаты с покупателями: скорость важна, Ozon учитывает время первого ответа.', levels: ['chat'] },
    { id: 'support', title: 'Техподдержка OZON', hint: 'Робот переписывается с поддержкой Ozon по вашим заданиям (вкладка «Техподдержка»).', levels: [] },
];

const AUTO_MODES = ['ai_auto', 'template'];

export default function FeedbackSettings({ accountId, onSaved, onOpen }) {
    const { request } = useApi();
    const [settings, setSettings] = useState(null);
    const [stopWords, setStopWords] = useState('');
    const [mustPhrases, setMustPhrases] = useState('');
    const [forbiddenPhrases, setForbiddenPhrases] = useState('');
    const [message, setMessage] = useState('');
    const [preview, setPreview] = useState(null);
    const [usage, setUsage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [historyNote, setHistoryNote] = useState('');

    useEffect(() => {
        let cancelled = false;
        loadFeedbackSettings({ request, accountId })
            .then((data) => {
                if (cancelled) return;
                setSettings(data);
                setStopWords((data.stop_words || []).join(', '));
                setMustPhrases((data.style?.must_phrases || []).join(', '));
                setForbiddenPhrases((data.style?.forbidden_phrases || []).join(', '));
            })
            .catch((error) => !cancelled && setMessage(errorText(error)));
        loadFeedbackUsage({ request, accountId }).then((data) => !cancelled && setUsage(data)).catch(() => {});
        return () => { cancelled = true; };
    }, [request, accountId]);

    if (!settings) return <div className="fb-card fb-muted">{message || 'Загрузка…'}</div>;

    const splitList = (value) => value.split(',').map((word) => word.trim()).filter(Boolean);
    const style = settings.style || {};
    const routing = settings.route_modes || {};
    const sections = routing.sections || {};
    const levelLabel = Object.fromEntries((settings.reply_levels || []).map((level) => [level.key, level.label]));
    const currentStyle = () => ({ ...style, must_phrases: splitList(mustPhrases), forbidden_phrases: splitList(forbiddenPhrases) });
    const setStyle = (field, value) => setSettings({ ...settings, style: { ...style, [field]: value } });
    const update = (field) => (event) => setSettings({ ...settings, [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value });
    const setRoute = (kind, level, value) => setSettings({
        ...settings,
        route_modes: { ...routing, [kind]: { ...(routing[kind] || {}), [level]: value } },
    });
    const features = settings.features || {};
    const available = (id) => features[id] !== false;   // инструмента нет в тарифе кабинета — блок выключен и недоступен
    const isOn = (id) => available(id) && (id === 'chat' ? Boolean(settings.chat_enabled) : sections[id] !== false);
    const toggle = (id, value) => (id === 'chat'
        ? setSettings({ ...settings, chat_enabled: value })
        : setSettings({ ...settings, route_modes: { ...routing, sections: { ...sections, [id]: value } } }));
    const select = (field, options) => (
        <select className="fb-input" value={style[field] || options[0][0]} onChange={(event) => setStyle(field, event.target.value)}>
            {options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
    );
    const labeled = (label, control) => <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><span className="fb-muted">{label}</span>{control}</label>;

    const chooseMode = (level, value) => {
        if (level === 'alarm' && AUTO_MODES.includes(value)) {
            const ok = window.confirm(
                'ВНИМАНИЕ. «Тревога» — это обращения со стоп-словами: юридические и медицинские темы, претензии, угрозы. '
                + 'Полный автомат отправит покупателю ответ БЕЗ вашей проверки. Ошибка ИИ здесь может обернуться жалобой, '
                + 'блокировкой или судом. Включить полный автомат на тревоге?',
            );
            if (!ok) return;
        }
        setRoute('modes', level, value);
    };

    const modeSelect = (level, showLabel = true) => {
        const mode = routing.modes?.[level] || 'draft';
        return (
            <div key={level} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {showLabel && <span className="fb-muted">{levelLabel[level] || level}</span>}
                <select className="fb-input fb-input--fill" value={mode} onChange={(event) => chooseMode(level, event.target.value)}>
                    {(settings.reply_modes || []).map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </select>
                {mode === 'template' && (
                    <textarea className="fb-textarea" rows={2} maxLength={1000} placeholder="Текст автоответа, одинаковый для всех обращений этого уровня"
                        value={routing.templates?.[level] || ''} onChange={(event) => setRoute('templates', level, event.target.value)} />
                )}
                {AUTO_MODES.includes(mode) && <div className="fb-warn">Ответ уйдёт покупателю без вашего подтверждения.</div>}
            </div>
        );
    };

    // Правила по темам общие для вопросов и чатов: показываются в обоих блоках, меняются в одном месте.
    const topicRules = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            <span className="fb-muted">Кто отвечает по темам (общее для вопросов и чатов)</span>
            {(settings.question_categories || []).map((category) => {
                const current = routing.categories?.[category.key] || 'inherit';
                return (
                    <label key={category.key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 13 }}>{category.label}</span>
                        <select className="fb-input fb-input--fill" value={current}
                            onChange={(event) => setSettings({ ...settings, route_modes: { ...routing, categories: { ...(routing.categories || {}), [category.key]: event.target.value } } })}>
                            {(settings.category_modes || []).map((mode) => <option key={mode.key} value={mode.key}>{mode.label}</option>)}
                        </select>
                        {current === 'ai_auto' && <span className="fb-warn">Ответ уйдёт без вашего подтверждения.</span>}
                    </label>
                );
            })}
            {labeled('Всегда отвечаю я, если в тексте есть (слова или фразы через запятую)',
                <textarea className="fb-textarea" rows={2} value={(routing.human_words || []).join(', ')}
                    onChange={(event) => setSettings({ ...settings, route_modes: { ...routing, human_words: splitList(event.target.value) } })} />)}
            <div className="fb-note">«Как в основном режиме» берёт режим из списка выше. Претензии разбирают правила и ИИ.</div>
        </div>
    );

    const save = async () => {
        try {
            const next = await saveFeedbackSettings({
                request, accountId,
                settings: {
                    // Сбор включён, если включён хотя бы один из разделов с агентом.
                    enabled: isOn('reviews') || isOn('questions') || isOn('chat'),
                    check_mode: settings.check_mode,
                    collect_since: settings.collect_since,
                    chat_enabled: settings.chat_enabled,
                    chat_poll_minutes: Number(settings.chat_poll_minutes || 5),
                    route_modes: settings.route_modes,
                    stop_words: splitList(stopWords),
                    style: currentStyle(),
                },
            });
            setSettings(next);
            setMessage('Сохранено');
            if (onSaved) onSaved();
        } catch (error) {
            setMessage(errorText(error));
        }
    };

    const loadHistory = async () => {
        setHistoryNote('');
        try {
            await saveFeedbackSettings({ request, accountId, settings: { collect_since: settings.collect_since } });
            const result = await collectFeedbackHistory({ request, accountId, since: settings.collect_since });
            setHistoryNote(result.scheduled
                ? `Поставлено ${result.scheduled} задач (по месяцу): агент соберёт отзывы с ${result.since}, начиная с самых свежих. Месяцы с очень большим числом отзывов делятся сами.`
                : 'История уже собирается: повторите, когда закончится');
        } catch (error) {
            setHistoryNote(errorText(error));
        }
    };

    const checkNow = async () => {
        setHistoryNote('');
        try {
            const result = await collectFeedbackNow({ request, accountId });
            setHistoryNote(result.scheduled?.length ? 'Проверка поставлена агенту' : 'Сбор уже идёт или ещё не прошёл интервал');
        } catch (error) {
            setHistoryNote(errorText(error));
        }
    };

    const runPreview = async () => {
        setBusy(true);
        setPreview(null);
        setMessage('');
        try {
            const data = await previewFeedbackStyle({ request, accountId, style: currentStyle() });
            setPreview(data.results || []);
        } catch (error) {
            setMessage(errorText(error));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fb">
            <FeedbackReadiness accountId={accountId} refreshKey={message} onOpen={onOpen || (() => {})} />

            <div className="fb-four">
                {SECTION_CARDS.map((card) => (
                    <div key={card.id} className={`fb-section${isOn(card.id) ? ' is-on' : ''}`}>
                        <label className="fb-section__head">
                            <input type="checkbox" checked={isOn(card.id)} disabled={!available(card.id)} onChange={(event) => toggle(card.id, event.target.checked)} />
                            <b>{card.title}</b>
                        </label>
                        <div className="fb-muted">{card.hint}</div>
                        {!available(card.id) && <div className="fb-warn">Этого инструмента нет в тарифе кабинета.</div>}
                        {isOn(card.id) && card.levels.map((level) => modeSelect(level, card.levels.length > 1))}
                        {isOn(card.id) && (card.id === 'questions' || card.id === 'chat') && topicRules()}
                        {card.id === 'chat' && isOn('chat') && labeled('Опрашивать новые чаты раз в, минут',
                            <input className="fb-input" type="number" min="1" max="60" value={settings.chat_poll_minutes} onChange={update('chat_poll_minutes')} />)}
                        {card.id === 'support' && isOn('support') && (
                            <div className="fb-note">Сколько сообщений в сутки и как часто проверять, вы задаёте в каждом задании. Робот пишет в обращение сам.</div>
                        )}
                    </div>
                ))}
            </div>

            <div className="fb-two">
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h4>Тревога (стоп-слова)</h4>
                    <div className="fb-muted">Обращения со стоп-словами: юридические и медицинские темы, претензии. Что с ними делать, решаете вы.</div>
                    {modeSelect('alarm', false)}
                    {labeled('Стоп-слова через запятую («=слово» — только целое слово)',
                        <textarea className="fb-textarea" rows={3} value={stopWords} onChange={(event) => setStopWords(event.target.value)} />)}

                    <h4 style={{ marginTop: 8 }}>Сбор отзывов и вопросов</h4>
                    {labeled('Собирать отзывы начиная с даты',
                        <input className="fb-input" type="date" value={settings.collect_since || ''} onChange={update('collect_since')} />)}
                    <div className="fb-row">
                        <button type="button" onClick={loadHistory} title="Собрать всё, что есть с этой даты: месяц за месяцем, свежие первыми">Собрать историю</button>
                        <button type="button" onClick={checkNow} title="Проверить новые отзывы и вопросы сейчас">Проверить сейчас</button>
                        {historyNote && <span className="fb-note">{historyNote}</span>}
                    </div>
                    {labeled('Как часто проверять новые отзывы и вопросы',
                        <select className="fb-input" value={settings.check_mode} onChange={update('check_mode')}>
                            {(settings.check_modes || []).map((mode) => (
                                <option key={mode.key} value={mode.key}>{mode.label}: {mode.hint}</option>
                            ))}
                        </select>)}
                    <div className="fb-note">
                        Проверка идёт, пока включён локальный агент: если компьютер был выключен, агент догонит пропущенное при запуске.
                        Кабинеты агент обходит по очереди: 20 кабинетов занимают около часа.
                        {settings.last_collect_ok_at ? ` Последняя удачная проверка: ${fmtDateTime(settings.last_collect_ok_at)}.` : ''}
                    </div>
                    <div className="fb-note">
                        Сбор и отправку делает локальный агент с входом в кабинет продавца. Платные подписки Ozon не используются.
                        {usage ? ` ИИ за 30 дней: ${usage.replies} ответов, ${usage.escalations} отказов, списано ${money(usage.charged_rub)}.` : ''}
                    </div>
                </div>
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h4>Стиль ответов ИИ</h4>
                    <div className="fb-note">Настройки меняют только тон и оформление. Правила площадки (без ссылок, телефонов, обещаний денег, просьб изменить отзыв) действуют всегда.</div>
                    {labeled('Название магазина или бренда', <input className="fb-input" maxLength={80} value={style.brand_name || ''} onChange={(event) => setStyle('brand_name', event.target.value)} />)}
                    {labeled('Подпись (пусто: без подписи)', <input className="fb-input" maxLength={200} value={style.signature || ''} onChange={(event) => setStyle('signature', event.target.value)} />)}
                    <div className="fb-row">
                        {labeled('Тональность', select('tone', TONE_OPTIONS))}
                        {labeled('Обращение', select('address', ADDRESS_OPTIONS))}
                        {labeled('Смайлики', select('emoji', EMOJI_OPTIONS))}
                        {labeled('Длина', select('length', LENGTH_OPTIONS))}
                    </div>
                    {labeled('Как говорит магазин (своими словами, до 500 знаков)',
                        <textarea className="fb-textarea" rows={3} maxLength={500} value={style.tone_notes || ''} onChange={(event) => setStyle('tone_notes', event.target.value)} />)}
                    <label><input type="checkbox" checked={Boolean(style.humanizer)} onChange={(event) => setStyle('humanizer', event.target.checked)} /> Humanizer: живой язык без канцелярита и шаблонных оборотов</label>
                    <label><input type="checkbox" checked={style.use_name !== false} onChange={(event) => setStyle('use_name', event.target.checked)} /> Обращаться по имени, если оно известно</label>
                    <label><input type="checkbox" checked={style.exclamations !== false} onChange={(event) => setStyle('exclamations', event.target.checked)} /> Разрешить восклицательные знаки</label>
                    {labeled('Фирменные формулировки (через запятую)', <input className="fb-input" value={mustPhrases} onChange={(event) => setMustPhrases(event.target.value)} />)}
                    {labeled('Слова и фразы, которые нельзя использовать (через запятую)', <input className="fb-input" value={forbiddenPhrases} onChange={(event) => setForbiddenPhrases(event.target.value)} />)}
                    {labeled('Политики магазина: что можно говорить о доставке, гарантии, обмене',
                        <textarea className="fb-textarea" rows={3} maxLength={1500} value={style.policies || ''} onChange={(event) => setStyle('policies', event.target.value)} />)}
                </div>
            </div>
            <div className="fb-actions">
                <button type="button" className="primary" onClick={save}>Сохранить</button>
                <button type="button" disabled={busy} onClick={runPreview} title="Три тестовых ответа с текущими настройками (бесплатно)">Предпросмотр стиля</button>
                {message && <span className="fb-note">{message}</span>}
            </div>
            {preview && (
                <div className="fb-list">
                    {preview.map((item) => (
                        <div key={item.case} className="fb-card">
                            <div className="fb-note">Покупатель: {item.case}</div>
                            <div className="fb-item__text">{item.text || `ИИ не стал отвечать: ${ESCALATION_LABELS[item.escalation] || 'нужен человек'}`}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
